import { useDbFunctions, useEvents } from '@/talons';
import pacote from '../../package.json';
import { useClient } from './ClientContext';
import { CardDescription, CardMode } from '@/types';
import { buildCartDetailsKey, getCartBrandKeyFromStorage, getOnStorage, getScopedPaymentConditionCodes, setOnStorage } from '@/shared';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';

interface HookProps {
  getDate: any;
  version: string;
  resetAccess: any;
  lastSync: string;
  routeTitle: string;
  paymentCode: number;
  fatherRouteTitle: string;
  cardMode: 'list' | 'grid';
  canAccessSuccess: boolean;
  canAccessCheckout: boolean;
  lastCheckedVersion: string;
  isOpenedResetPasswordModal: boolean;
  currentModal: string;
  activeTheme?: any;
  forcedReloadTables: boolean;
  orderData: any;
  isExactSearch: boolean;
  location: Location;
  cartTotals: any;
  orderItems: any;
  scrollPosition: number;
  cardDescription: CardDescription;
  downloadingFiles: string[];
  startedDownloadingFiles: string[];
  downloadSpeedBps: number;
  fileDownload: string;
  fileDownloadProgress: string;
  progressPercent: number;
  downloadingFileName: string;
  scrollDirection: 'up' | 'down' | null;
  setCurrentModal: (value: string) => void;
  setDownloadingFileName: (value: string) => void;
  setDownloadingFiles: (value: string[]) => void;
  setCardDescription: (value: CardDescription) => void;
  setStartDownloadingFile: (value: string) => void;
  setForcedReloadTables: (value: boolean) => void;
  setVersion: (value: string) => void;
  setLastSync: (value: string) => void;
  setRouteTitle: (value: string) => void;
  setIsExactSearch: (value: boolean) => void;
  setPaymentCode: (value: number) => void;
  setFatherRouteTitle: (value: string) => void;
  setCardMode: (value: 'list' | 'grid') => void;
  setCanAccessSuccess: (value: boolean) => void;
  setCanAccessCheckout: (value: boolean) => void;
  setLastCheckedVersion: (value: string) => void;
  setIsOpenResetPasswordModal: (value: boolean) => void;
  setActiveTheme?: (value: any) => void;
  setFileDownload: (value: string) => void;
  setFileDownloadProgress: (value: string) => void;
  setProgressPercent: (value: number) => void;
  setOrderData: (value: any) => void;
  setCartTotals: (value: any) => void;
  setOrderItems: (value: any) => void;
  setDownloadSpeedBps: (value: number) => void;
  setLocation: (value: Location) => void;
  setScrollPosition: (value: number) => void;
  setScrollDirection: (value: 'up' | 'down' | null) => void;
}

const ConfigsContex = createContext<HookProps>({} as HookProps);
const DEFAULT_PAYMENT_CODE = 14;

const GlobalProvider: React.FC<any> = ({ children }) => {
  const db = useDbFunctions();
  const { registerEvent } = useEvents(db);
  const { selectedClient: client } = useClient();
  const [routeTitle, setHookRouteTitle] = useState('');
  const [currentModal, setCurrentModal] = useState('');
  const [orderData, setOrderData] = useState<any>(null);
  const [cartTotals, setCartTotals] = useState<any>(null);
  const [orderItems, setOrderItems] = useState<any>(null);
  const [activeTheme, setActiveTheme] = useState<any>(null);
  const [version, setHookVersion] = useState(pacote.version);
  const [fatherRouteTitle, setHookFatherTitle] = useState('');
  const [scrollPosition, setScrollPosition] = useState<number>(0);
  const [canAccessSuccess, setCanAccessSuccess] = useState(false);
  const [canAccessCheckout, setCanAccessCheckout] = useState(false);
  const [downloadSpeedBps, setDownloadSpeedBps] = useState<number>(0);
  const [downloadingFileName, setDownloadingFileNameHook] = useState('');
  const [forcedReloadTables, setForcedReloadTables] = useState<boolean>(false);
  const [downloadingFiles, setDownloadingFilesHook] = useState<string[]>([]);
  const [isOpenedResetPasswordModal, setIsOpenResetPasswordModal] = useState(false);
  const [scrollDirection, setScrollDirection] = useState<'up' | 'down' | null>(null);
  const [startedDownloadingFiles, setStartDownloadingFileHook] = useState<string[]>([]);
  const [isExactSearch, setIsExactSearchHook] = useState(getOnStorage('isExactSearch') ?? false);
  const [selectedBrandKey, setSelectedBrandKey] = useState(() => getCartBrandKeyFromStorage());
  const [paymentCode, setHookPaymentCode] = useState(() => {
    const paymentCodes = getOnStorage('paymentCode') || {};
    const scopedKey = buildCartDetailsKey(client?.COD_CLIENTE, getCartBrandKeyFromStorage());
    return paymentCodes?.[scopedKey] || paymentCodes?.[client?.COD_CLIENTE] || DEFAULT_PAYMENT_CODE;
  });
  const [fileDownload, setFileDownload] = useState<string>('0kb');
  const [fileDownloadProgress, setFileDownloadProgress] = useState<string>('0kb');
  const [progressPercent, setProgressPercent] = useState<number>(0);
  const [location, setLocation] = useState<Location>(window.location);
  const [cardMode, setHookCardMode] = useState(() => getOnStorage('cardMode') ?? ('list' as CardMode));
  const [lastSync, setHookLastSync] = useState(() => getOnStorage('lastSync') ?? 'Nenhuma atualização');
  const [lastCheckedVersion, setHookLastCheckedVersion] = useState(() => getOnStorage('lastCheckedVersion') ?? '');
  const [cardDescription, setHookCardDescription] = useState(() => getOnStorage('cardDescription') ?? CardDescription.DC);
  
  const setRouteTitle = useCallback((title: string) => setHookRouteTitle(title), [setHookRouteTitle]);

  const setDownloadingFileName = (fileName: string) => fileName && setDownloadingFileNameHook(fileName);

  const setDownloadingFiles = (value: string[]) => value && setDownloadingFilesHook(value);

  const setIsExactSearch = useCallback(
    (isExactSearch: boolean) => {
      setIsExactSearchHook(isExactSearch);
      setOnStorage('isExactSearch', isExactSearch);
    },
    [setIsExactSearchHook]
  );

  const setPaymentCode = useCallback(
    async (value: number) => {
      if (!client?.COD_CLIENTE) return;
      const allowed = getScopedPaymentConditionCodes(client);
      const num = Number(value);
      const next = allowed.includes(num)
        ? num
        : Number(allowed[0] ?? client?.COD_PAGAMENTO?.[0]?.cod_condicao ?? DEFAULT_PAYMENT_CODE);
      setHookPaymentCode(next);
      const paymentCodes = getOnStorage('paymentCode') || {};
      const scopeKey = buildCartDetailsKey(client.COD_CLIENTE, selectedBrandKey);
      setOnStorage('paymentCode', {
        ...paymentCodes,
        [scopeKey]: next,
      });
      await registerEvent({
        tipoEvento: 'PAYMENT_CODE',
        obsEvento: `[cod_pagamento]: ${next}`,
      });
    },
    [setHookPaymentCode, client, selectedBrandKey]
  );

  useEffect(() => {
    if (!client?.COD_CLIENTE) return;

    const paymentCodes = getOnStorage('paymentCode') || {};
    const scopeKey = buildCartDetailsKey(client.COD_CLIENTE, selectedBrandKey);
    if (!!paymentCodes[scopeKey]) return;

    const allowed = getScopedPaymentConditionCodes(client);
    const padrao = client.COD_CONDICAO_PADRAO;
    const next =
      padrao != null && allowed.includes(Number(padrao))
        ? Number(padrao)
        : allowed[0] ??
          client?.COD_PAGAMENTO?.[0]?.cod_condicao ??
          DEFAULT_PAYMENT_CODE;
    setPaymentCode(next);
  }, [client, selectedBrandKey, setPaymentCode]);

  const setVersion = useCallback((version: string) => setHookVersion(version), [setHookVersion]);

  const setLastSync = useCallback(
    (lastSync: string) => {
      setHookLastSync(lastSync);
      setOnStorage('lastSync', lastSync);
    },
    [setHookLastSync]
  );

  const setFatherRouteTitle = useCallback((fatherTitle: string) => setHookFatherTitle(fatherTitle), [setHookFatherTitle]);

  const setCardMode = useCallback(
    (cardMode: CardMode) => {
      setHookCardMode(cardMode);
      setOnStorage('cardMode', cardMode);
    },
    [setHookCardMode]
  );

  const setCardDescription = useCallback(
    (cardDescription: CardDescription) => {
      setHookCardDescription(cardDescription);
      setOnStorage('cardDescription', cardDescription);
    },
    [setHookCardMode]
  );

  const setLastCheckedVersion = useCallback(
    (lastCheckedVersion: string) => {
      setHookLastCheckedVersion(lastCheckedVersion);
      setOnStorage('lastCheckedVersion', lastCheckedVersion);
    },
    [setHookLastCheckedVersion]
  );

  useEffect(() => {
    const updateBrandKey = () => {
      setSelectedBrandKey(getCartBrandKeyFromStorage());
    };

    window.addEventListener('selected-brand-changed', updateBrandKey);
    window.addEventListener('storage', updateBrandKey);
    return () => {
      window.removeEventListener('selected-brand-changed', updateBrandKey);
      window.removeEventListener('storage', updateBrandKey);
    };
  }, []);

  useEffect(() => {
    if (!client?.COD_CLIENTE) return;
    const paymentCodes = getOnStorage('paymentCode') || {};
    const scopeKey = buildCartDetailsKey(client.COD_CLIENTE, selectedBrandKey);
    const storedPaymentCode = paymentCodes?.[scopeKey];
    const paymentCode =
      storedPaymentCode ||
      client?.COD_CONDICAO_PADRAO ||
      client?.COD_PAGAMENTO?.[0]?.cod_condicao ||
      DEFAULT_PAYMENT_CODE;
    const allowed = getScopedPaymentConditionCodes(client);
    if (allowed.includes(Number(paymentCode))) {
      setHookPaymentCode(Number(paymentCode));
      return;
    }
    const fallback =
      allowed[0] ?? client?.COD_PAGAMENTO?.[0]?.cod_condicao ?? DEFAULT_PAYMENT_CODE;
    setHookPaymentCode(Number(fallback));
  }, [client, selectedBrandKey]);

  const resetAccess = () => {
    setCanAccessCheckout(false);
    setCanAccessSuccess(false);
  };

  const addZero = (numero: number) => (numero < 10 ? `0${numero}` : numero);

  const getDate = () => {
    const agora = new Date();
    const dia = addZero(agora.getDate());
    const mes = addZero(agora.getMonth() + 1); // Os meses em JavaScript são baseados em zero
    const ano = agora.getFullYear();
    const horas = addZero(agora.getHours());
    const minutos = addZero(agora.getMinutes());

    return `${dia}/${mes}/${ano} às ${horas}:${minutos}`;
  };

  const setStartDownloadingFile = (fileName: string) => fileName && setStartDownloadingFileHook((prev) => {
    return [...prev, fileName];
  });

  return (
    <ConfigsContex.Provider
      value={{
        startedDownloadingFiles,
        setStartDownloadingFile,

        location,
        setLocation,

        scrollDirection,
        setScrollDirection,
        paymentCode,
        setPaymentCode,
        downloadingFiles,
        setDownloadingFiles,
        canAccessCheckout,
        canAccessSuccess,

        downloadingFileName,
        setDownloadingFileName,

        fileDownload,
        setFileDownload,

        fileDownloadProgress,
        setFileDownloadProgress,

        progressPercent,
        setProgressPercent,

        downloadSpeedBps,
        setDownloadSpeedBps,

        isExactSearch,
        setIsExactSearch,

        getDate,
        lastSync,
        setLastSync,

        version,
        setVersion,

        cardMode,
        setCardMode,

        routeTitle,
        setRouteTitle,

        cardDescription,
        setCardDescription,

        fatherRouteTitle,
        setFatherRouteTitle,
        
        forcedReloadTables, 
        setForcedReloadTables,

        setCanAccessCheckout,
        setCanAccessSuccess,
        resetAccess,

        isOpenedResetPasswordModal,
        setIsOpenResetPasswordModal,

        lastCheckedVersion,
        setLastCheckedVersion,

        activeTheme,
        setActiveTheme,

        orderData,
        setOrderData,
        cartTotals,
        setCartTotals,
        orderItems,
        setOrderItems,

        scrollPosition,
        setScrollPosition,

        currentModal,
        setCurrentModal,
      }}
    >
      <div id="version-id" style={{ display: 'none' }}>
        {version}
      </div>
      {children}
    </ConfigsContex.Provider>
  );
};

const useGlobals = (): HookProps => {
  const ctx = useContext(ConfigsContex);

  if (!ctx) throw new Error('Erro ao usar hook Header');

  return ctx;
};

export { GlobalProvider, useGlobals };
